import { Divider, Icon, MultiSlider } from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomTag from "components/Atoms/CustomTag"
import { flatten, max, startCase, uniq } from "lodash-es"
import React, { useCallback, useState } from "react"
import classNames from "utils/classNames"
import { readableHour } from "utils/date-utils"
import useDCStore from "./dc.store"
import useDCRemoveM2M from "./useDCRemoveM2M"

const TimeSlider = ({
  id,
  title = undefined,
  className = undefined,
  children = undefined,
}) => {
  const [showChildren, setShowChildren] = useState(false)
  const { values, disabled, onChange, onAdd, onRemove } = useDCStore(
    useCallback(
      store => ({
        values: store.state.operationalSlots?.[id] ?? [],
        disabled: store.disabled,
        onChange: store.setData("operationalSlots"),
        onAdd: store.addListItem,
        onRemove: store.removeListItem,
      }),
      [id]
    )
  )

  const { loading, removeLink } = useDCRemoveM2M()
  if (!values || !Array.isArray(values)) return null

  return (
    <div className={classNames("vt-operating-hours", className)}>
      <div className="flex items-center">
        <span className="vt-highlight mr-2">{title ?? startCase(id)} :</span>
        <CustomButton
          disabled={disabled}
          icon="plus"
          intent="primary"
          className="rounded-full mr-2"
          onClick={
            disabled
              ? null
              : () => {
                  const flat = uniq(flatten(values?.map(v => v.slots)))
                  const maxHour = max(flat)
                  if (!maxHour) {
                    onAdd(`operationalSlots.${id}`, { slots: [10, 18] })
                  }
                  if (maxHour < 23) {
                    onAdd(`operationalSlots.${id}`, {
                      slots: [maxHour, maxHour + 2],
                    })
                  }
                }
          }
        />
        {values.map((value, idx) => {
          const slot = value?.slots
          const slotId = value?.id
          return (
            <CustomTag
              large
              className="mr-2"
              key={idx}
              round
              interactive={disabled || loading ? false : true}
              minimal
              onRemove={() => {
                if (disabled || loading) return null
                if (slotId) {
                  removeLink({ fieldName: id, fieldIds: [slotId] })
                }
                onRemove(`operationalSlots.${id}`, idx, true)
              }}
            >
              {slot?.map?.(hour => readableHour(hour))?.join("-")}
            </CustomTag>
          )
        })}
        {children && (
          <Icon
            aria-expanded={showChildren}
            className="vt-dropdown"
            icon="chevron-down"
            onClick={() => setShowChildren(prev => !prev)}
          />
        )}
      </div>
      <Divider className="mt-2 mb-3" />
      <MultiSlider
        disabled={disabled}
        labelRenderer={num => {
          if (num % 3 === 0 || num === 1) return readableHour(num)
          for (const value of values) {
            if (value?.[0] === num || value?.[1] === num)
              return readableHour(num)
          }
          return "."
        }}
        min={1}
        max={24}
        stepSize={0.5}
        showTrackFill={true}
      >
        {values.map((value, index) => {
          const h = value?.slots
          return h?.map?.((h, idx) => {
            return (
              <MultiSlider.Handle
                className="vt-slider-handle"
                onChange={value =>
                  onChange(`${id}.${index}.slots.${idx}`, value)
                }
                key={`${index}-${idx}`}
                value={h === 0 ? 24 : h}
                type={idx === 0 ? "start" : "end"}
                intentAfter={idx === 0 ? "primary" : undefined}
                interactionKind="lock"
              />
            )
          })
        })}
      </MultiSlider>
      {showChildren ? children : null}
    </div>
  )
}

export default TimeSlider
